import type { PixelBuffer } from './buffer';
import { cloneBuffer, premultiply, unpremultiply } from './buffer';

/**
 * Blur and sharpen.
 *
 * Three box-blur passes approximate a Gaussian in O(n) per pixel instead of
 * O(n·r²) for a naive kernel — the difference between a slider that tracks the
 * pointer and one that stutters on a large layer.
 *
 * Blurring happens on premultiplied pixels: blurring straight alpha drags the
 * colour of transparent regions into visible ones and gives every edge a dark
 * halo.
 */

const CHANNEL_COUNT = 4;

function boxBlurPass(
  source: Uint8ClampedArray,
  target: Uint8ClampedArray,
  width: number,
  height: number,
  radius: number,
  horizontal: boolean,
): void {
  const span = radius * 2 + 1;
  const lines = horizontal ? height : width;
  const line = horizontal ? width : height;

  for (let lineIndex = 0; lineIndex < lines; lineIndex += 1) {
    // Sliding window sums, so moving one pixel costs one add and one subtract.
    const sums = new Int32Array(CHANNEL_COUNT);
    const offsetOf = (index: number) =>
      (horizontal ? lineIndex * width + index : index * width + lineIndex) * 4;

    for (let i = -radius; i <= radius; i += 1) {
      const clamped = Math.min(line - 1, Math.max(0, i));
      const at = offsetOf(clamped);
      for (let channel = 0; channel < CHANNEL_COUNT; channel += 1) {
        sums[channel] = (sums[channel] ?? 0) + (source[at + channel] ?? 0);
      }
    }

    for (let index = 0; index < line; index += 1) {
      const at = offsetOf(index);
      for (let channel = 0; channel < CHANNEL_COUNT; channel += 1) {
        target[at + channel] = (sums[channel] ?? 0) / span;
      }
      const addIndex = Math.min(line - 1, index + radius + 1);
      const removeIndex = Math.max(0, index - radius);
      const addAt = offsetOf(addIndex);
      const removeAt = offsetOf(removeIndex);
      for (let channel = 0; channel < CHANNEL_COUNT; channel += 1) {
        sums[channel] =
          (sums[channel] ?? 0) + (source[addAt + channel] ?? 0) - (source[removeAt + channel] ?? 0);
      }
    }
  }
}

/** In-place box blur, three passes, on a premultiplied buffer. */
function boxBlur(buffer: PixelBuffer, radius: number): void {
  const { width, height, data } = buffer;
  const scratch = new Uint8ClampedArray(data.length);
  for (let pass = 0; pass < 3; pass += 1) {
    boxBlurPass(data, scratch, width, height, radius, true);
    boxBlurPass(scratch, data, width, height, radius, false);
  }
}

/**
 * Blur a buffer by radius in pixels.
 *
 * Returns a new buffer; the source is left untouched so a preview pass can be
 * thrown away without re-decoding the layer.
 */
export function blurBuffer(source: PixelBuffer, radius: number): PixelBuffer {
  const result = cloneBuffer(source);
  const r = Math.max(0, Math.round(radius));
  if (r === 0) return result;
  premultiply(result);
  boxBlur(result, r);
  unpremultiply(result);
  return result;
}

/**
 * Unsharp mask: add back the difference between the pixel and its blurred self.
 *
 * Blur first, then `original + amount × (original − blurred)`. Radius controls
 * how wide the edge is, amount controls how hard it is pushed.
 */
export function sharpenBuffer(source: PixelBuffer, amount: number, radius = 1): PixelBuffer {
  const blurred = blurBuffer(source, radius);
  const result = cloneBuffer(source);
  const { data } = result;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;
    for (let channel = 0; channel < 3; channel += 1) {
      const original = source.data[i + channel] ?? 0;
      const edge = original - (blurred.data[i + channel] ?? 0);
      data[i + channel] = original + edge * amount;
    }
  }
  return result;
}
